import { CalendarIcon, Pencil1Icon } from "@radix-ui/react-icons";
import { Row, Table } from "@tanstack/react-table";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog/alert-dialog";
import { Button } from "@/components/ui/button/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";

import { Input } from "@/components/ui/input/input";
import { Calendar } from "@/components/ui/calendar/calendar";

import { HolidayList } from "./schema";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface UpdateRowModalProps<TData> {
  row: Row<TData>;
  table: Table<TData>;
}

export const UpdateRowModal = <TData,>({
  row,
  table,
}: UpdateRowModalProps<TData>) => {
  const originData = row.original as HolidayList;

  const [rowData, setRowData] = useState<{
    holidayName: string;
    description: string;
    date: Date;
  }>(() => originData);

  const [open, setOpen] = useState(false);

  return (
    <>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger>
          <span className="cursor-pointer transition-all duration-300">
            <Pencil1Icon className="w-6 h-6 transition-all duration-300 p-1 hover:bg-ring/10 rounded-full" />
          </span>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Row</AlertDialogTitle>
          </AlertDialogHeader>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="holidayName">Holiday Name</label>
              <Input
                id="holidayName"
                name="holidayName"
                value={rowData.holidayName}
                type="text"
                onChange={(e) => {
                  setRowData((prev) => {
                    return {
                      ...prev,
                      [e.target.name]: e.target.value,
                    };
                  });
                }}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="description">Description</label>
              <Input
                id="description"
                name="description"
                value={rowData.description}
                type="text"
                onChange={(e) => {
                  setRowData((prev) => {
                    return {
                      ...prev,
                      [e.target.name]: e.target.value,
                    };
                  });
                }}
              />
            </div>
            <div>
              <Popover>
                <span>Date</span>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "w-full pl-3 text-left font-normal",
                      !rowData.date && "text-muted-foreground"
                    )}
                  >
                    {rowData.date ? (
                      format(rowData.date, "PPP")
                    ) : (
                      <span>Pick a date</span>
                    )}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={rowData.date}
                    onSelect={(day) => {
                      setRowData((prev) => {
                        return {
                          ...prev,
                          date: day as Date,
                        };
                      });
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // @ts-ignore
                table.options.meta?.updateRow(row.index, rowData);

                setOpen(false);
              }}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
